import { useId } from 'react';
import type { ApplicationPlatform } from '../../data/application';

function AppStoreIcon() {
  const gradientId = useId();

  return (
    <svg
      className="platform-download-btn__icon platform-download-btn__icon--app-store"
      viewBox="0 0 800 800"
      aria-hidden
    >
      <defs>
        <linearGradient id={gradientId} x1="400.05" y1="798.77" x2="400.05" y2="-1.23" gradientTransform="matrix(1 0 0 -1 0 798.77)">
          <stop offset="0" stopColor="#18BFFB" />
          <stop offset="1" stopColor="#2072F3" />
        </linearGradient>
      </defs>
      <path
        fill={`url(#${gradientId})`}
        d="M638.4,0H161.6C72.3,0,0,72.3,0,161.6v476.9C0,727.7,72.3,800,161.6,800h476.9c89.2,0,161.6-72.3,161.6-161.6V161.6C800,72.3,727.7,0,638.4,0z"
      />
      <path
        fill="#FFFFFF"
        d="M396.6,183.8l16.2-28c10-17.5,32.3-23.4,49.8-13.4s23.4,32.3,13.4,49.8L319.9,462.4h112.9c36.6,0,57.1,43,41.2,72.8H143c-20.2,0-36.4-16.2-36.4-36.4c0-20.2,16.2-36.4,36.4-36.4h92.8l118.8-205.9l-37.1-64.4c-10-17.5-4.1-39.6,13.4-49.8c17.5-10,39.6-4.1,49.8,13.4L396.6,183.8z M256.2,572.7l-35,60.7c-10,17.5-32.3,23.4-49.8,13.4S148,614.5,158,597l26-45C213.4,542.9,237.3,549.9,256.2,572.7z M557.6,462.6h94.7c20.2,0,36.4,16.2,36.4,36.4c0,20.2-16.2,36.4-36.4,36.4h-52.6l35.5,61.6c10,17.5,4.1,39.6-13.4,49.8c-17.5,10-39.6,4.1-49.8-13.4c-59.8-103.7-104.7-181.3-134.5-233c-30.5-52.6-8.7-105.4,12.8-123.3C474.2,318.1,509.9,380,557.6,462.6z"
      />
    </svg>
  );
}

function GooglePlayIcon() {
  return (
    <svg
      className="platform-download-btn__icon platform-download-btn__icon--google-play"
      viewBox="0 0 24 24"
      aria-hidden
    >
      <path
        fill="#EA4335"
        d="M3 20.5V3.5C3 2.91 3.34 2.39 3.84 2.15L13.69 12 3.84 21.85C3.34 21.6 3 21.09 3 20.5Z"
      />
      <path fill="#FBBC04" d="M16.81 15.12 6.05 21.34 14.54 12.85 16.81 15.12Z" />
      <path
        fill="#4285F4"
        d="M20.16 10.81c.34.28.59.68.59 1.19s-.25.91-.59 1.19l-2.27 1.32-2.5-2.5 2.5-2.5 2.27 1.32Z"
      />
      <path fill="#34A853" d="M6.05 2.66 16.81 8.88 14.54 11.15 6.05 2.66Z" />
    </svg>
  );
}

function DownloadIcon({ store }: { store: ApplicationPlatform['device'] }) {
  if (store === 'android') {
    return <GooglePlayIcon />;
  }

  return <AppStoreIcon />;
}

interface PlatformDownloadButtonProps {
  platform: ApplicationPlatform;
}

export function PlatformDownloadButton({ platform }: PlatformDownloadButtonProps) {
  const { download } = platform;
  const external = download.href.startsWith('http');
  const badgeImage = 'badgeImage' in download ? download.badgeImage : undefined;

  return (
    <div className="platform-section__download">
      <a
        href={download.href}
        className={badgeImage ? 'platform-download-badge' : 'platform-download-btn'}
        aria-label={download.label}
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {badgeImage ? (
          <img
            src={badgeImage}
            alt={download.label}
            className="platform-download-badge__image"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <>
            <DownloadIcon store={platform.device} />
            <span className="platform-download-btn__text">
              <span className="platform-download-btn__sublabel">{download.sublabel}</span>
              <span className="platform-download-btn__label">{download.label}</span>
            </span>
          </>
        )}
      </a>
    </div>
  );
}
